import { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Target,
  Telescope,
  Hammer,
  ShieldCheck,
  Lightbulb,
  HeartHandshake,
  Users,
  TrendingUp,
  Check,
  X,
  Building2,
  Briefcase,
  UtensilsCrossed,
  ShoppingBag,
  Home as HomeIcon,
  Hotel,
  Stethoscope,
} from "lucide-react";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { WhatsAppFloat } from "@/components/site/WhatsAppFloat";
import { QuoteDialog } from "@/components/site/QuoteDialog";
import { FinalCTA } from "@/components/site/FinalCTA";
import { CountUp } from "@/components/site/CountUp";
import { Reveal } from "@/components/site/Reveal";

const HERO = "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=2400&q=85";
const WHO_IMG = "https://images.unsplash.com/photo-1581094288338-2314dddb7ece?auto=format&fit=crop&w=1600&q=85";

const COMPARE = {
  others: [
    "Only one service offered",
    "Different subcontractors per stage",
    "Limited post-handover support",
    "Project ends after completion",
    "Multiple points of contact",
  ],
  us: [
    "Turnkey property solutions",
    "Dedicated project management",
    "Long-term facility maintenance",
    "Professional cleaning included",
    "Holiday home furnishing",
    "Authority approvals handled",
    "One single point of contact",
  ],
};

const VALUES = [
  { icon: Hammer, title: "Craftsmanship", body: "Every finish reflects precision and care." },
  { icon: ShieldCheck, title: "Integrity", body: "Transparent communication and honest advice." },
  { icon: Lightbulb, title: "Innovation", body: "Modern techniques and efficient solutions." },
  { icon: HeartHandshake, title: "Reliability", body: "Projects delivered with professionalism and accountability." },
  { icon: Users, title: "Customer First", body: "Every decision is made around client satisfaction." },
  { icon: TrendingUp, title: "Continuous Improvement", body: "Always improving our systems, people and quality." },
];

const INDUSTRIES = [
  { icon: HomeIcon, label: "Residential", desc: "Apartment owners, villa owners, homeowners", img: "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?auto=format&fit=crop&w=1200&q=80" },
  { icon: Briefcase, label: "Commercial", desc: "Offices, retail units, showrooms", img: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1200&q=80" },
  { icon: Hotel, label: "Hospitality", desc: "Hotels, serviced apartments, holiday home operators", img: "https://images.unsplash.com/photo-1455587734955-081b22074882?auto=format&fit=crop&w=1200&q=80" },
  { icon: UtensilsCrossed, label: "Food & Beverage", desc: "Restaurants, cafes, cloud kitchens", img: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=1200&q=80" },
  { icon: Stethoscope, label: "Healthcare", desc: "Clinics, medical centres, pharmacies", img: "https://images.unsplash.com/photo-1629909613654-28e377c37b09?auto=format&fit=crop&w=1200&q=80" },
  { icon: ShoppingBag, label: "Education", desc: "Schools, nurseries, training centres", img: "https://images.unsplash.com/photo-1580582932707-520aed937b7b?auto=format&fit=crop&w=1200&q=80" },
  { icon: ShieldCheck, label: "Government", desc: "Government offices and public facilities", img: "https://images.unsplash.com/photo-1541746972996-4e0b0f43e02a?auto=format&fit=crop&w=1200&q=80" },
  { icon: Building2, label: "Developers", desc: "New residential and commercial developments", img: "https://images.unsplash.com/photo-1582407947304-fd86f028f716?auto=format&fit=crop&w=1200&q=80" },
  { icon: Users, label: "Property Managers", desc: "FM companies and property management firms", img: "https://images.unsplash.com/photo-1581092160562-40aa08e78837?auto=format&fit=crop&w=1200&q=80" },
];

const PROCESS = [
  "Consultation",
  "Site Survey",
  "Planning",
  "Execution",
  "Inspection",
  "Handover",
  "Aftercare",
];

export default function About() {
  const [quoteOpen, setQuoteOpen] = useState(false);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], [0, 140]);
  const heroScale = useTransform(scrollY, [0, 600], [1.05, 1.15]);

  useEffect(() => {
    document.title = "About Bloc & Brush | Interior Fit-Out & Property Solutions Dubai";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-white">
      <Navbar onQuoteClick={() => setQuoteOpen(true)} />

      {/* HERO */}
      <section
        data-testid="about-hero"
        className="relative min-h-[88vh] flex items-end overflow-hidden"
      >
        <motion.div style={{ y: heroY, scale: heroScale }} className="absolute inset-0">
          <img src={HERO} alt="Luxury Dubai interior" className="w-full h-[120%] object-cover" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1A33]/25 via-[#0B1A33]/25 to-[#0B1A33]/80" />

        <div className="relative container mx-auto px-6 lg:px-12 max-w-7xl pt-32 pb-20 lg:pb-28 text-white">
          <motion.span
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-xs uppercase tracking-[0.28em] text-white/70"
          >
            About Bloc & Brush
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.15 }}
            className="font-display mt-6 text-5xl md:text-6xl lg:text-[78px] font-extrabold leading-[0.98] tracking-tight max-w-5xl"
          >
            Building Better Spaces.
            <br />
            <span className="text-white/65">Creating Better Experiences.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="mt-8 text-base lg:text-[18px] text-white/75 max-w-2xl leading-relaxed"
          >
            Bloc & Brush is a Dubai-based turnkey fit-out, furnishing, maintenance
            and cleaning company committed to exceptional workmanship, reliable
            execution and long-term property solutions — all under one roof.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.45 }}
            className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4"
          >
            <button
              onClick={() => setQuoteOpen(true)}
              data-testid="about-hero-quote-btn"
              className="inline-flex items-center justify-center gap-2 bg-white text-[#13294B] hover:bg-white/90 rounded-full px-7 py-4 text-[15px] font-medium transition"
            >
              Get Free Quote <ArrowRight size={16} />
            </button>
            <Link
              to="/services"
              data-testid="about-hero-services-btn"
              className="inline-flex items-center justify-center gap-2 border border-white/30 hover:bg-white/10 rounded-full px-7 py-4 text-[15px] font-medium transition"
            >
              Explore Services
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 0.9 }}
            className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
          >
            <span className="text-[11px] uppercase tracking-[0.3em] text-white/55">Scroll</span>
            <div className="w-px h-10 bg-white/40 overflow-hidden relative">
              <motion.div
                animate={{ y: [-40, 40] }}
                transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
                className="w-px h-6 bg-white absolute"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* OUR STORY */}
      <section className="py-24 lg:py-36 bg-white relative overflow-hidden" data-testid="about-story">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <Reveal className="lg:col-span-5">
              <span className="text-xs uppercase tracking-[0.22em] text-[#707070]">Our Story</span>
              <h2 className="font-display mt-4 text-4xl lg:text-[56px] font-extrabold leading-[1.02] tracking-tight text-[#1C1C1C]">
                How Bloc & Brush
                <br />
                <span className="text-[#13294B]">Was Built.</span>
              </h2>
              <div className="mt-8 space-y-5 text-[17px] leading-[1.8] text-[#1C1C1C]/75">
                <p>
                  Modern properties require far more than beautiful interiors.
                  Owners today need a reliable partner capable of handling
                  everything — from structural works and technical installations
                  to furnishing, maintenance and cleaning.
                </p>
                <p>
                  Bloc & Brush was established to solve this challenge. Our
                  vision was simple: create one company capable of delivering
                  every essential service required to transform, maintain and
                  protect a property.
                </p>
                <p>
                  Today we proudly support homeowners, investors, developers,
                  Airbnb operators and commercial businesses throughout Dubai.
                  By combining technical expertise with premium craftsmanship,
                  we help clients maximise property value while reducing the
                  complexity of managing multiple contractors.
                </p>
              </div>

              <div className="mt-10 flex flex-wrap gap-2.5">
                {["Integrity", "Quality", "Transparency", "Attention to Detail", "Long-Term Relationships"].map((p) => (
                  <span key={p} className="px-4 py-2 rounded-full bg-[#F5F5F5] text-[#13294B] text-[13px] font-medium border border-[#13294B]/10">
                    {p}
                  </span>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.15} className="lg:col-span-7">
              <div className="relative">
                <div className="grid grid-cols-6 grid-rows-6 gap-3 lg:gap-4 aspect-[6/6]">
                  <div className="col-span-4 row-span-4 overflow-hidden rounded-3xl">
                    <img
                      src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1400&q=85"
                      alt="Luxury Dubai interior"
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1.4s] ease-out"
                    />
                  </div>
                  <div className="col-span-2 row-span-3 overflow-hidden rounded-3xl">
                    <img
                      src="https://images.unsplash.com/photo-1581094288338-2314dddb7ece?auto=format&fit=crop&w=800&q=85"
                      alt="Engineers on site"
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1.4s] ease-out"
                    />
                  </div>
                  <div className="col-span-2 row-span-3 overflow-hidden rounded-3xl">
                    <img
                      src="https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?auto=format&fit=crop&w=800&q=85"
                      alt="Completed luxury villa"
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1.4s] ease-out"
                    />
                  </div>
                  <div className="col-span-4 row-span-2 overflow-hidden rounded-3xl">
                    <img
                      src="https://images.unsplash.com/photo-1616594039964-ae9021a400a0?auto=format&fit=crop&w=1400&q=85"
                      alt="Furnished holiday home"
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1.4s] ease-out"
                    />
                  </div>
                </div>

                <div className="absolute -bottom-6 -left-6 lg:-left-10 bg-[#13294B] text-white rounded-2xl p-6 shadow-[0_20px_60px_rgba(19,41,75,0.25)] max-w-[240px]">
                  <div className="font-display text-3xl font-extrabold leading-none">12+</div>
                  <div className="text-[13px] text-white/75 mt-2 leading-relaxed">
                    Years of combined craftsmanship across Dubai's most demanding properties.
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* LEADERSHIP / FOUNDERS */}
      <section className="py-24 lg:py-32 bg-white" data-testid="about-leadership">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <Reveal>
            <div className="max-w-3xl">
              <span className="text-xs uppercase tracking-[0.22em] text-[#707070]">Leadership</span>
              <h2 className="font-display mt-4 text-4xl lg:text-[48px] font-extrabold leading-[1.05] tracking-tight text-[#1C1C1C]">
                Meet The People
                <br />
                <span className="text-[#13294B]">Who Sign Things Off.</span>
              </h2>
              <p className="mt-6 text-[17px] leading-[1.8] text-[#1C1C1C]/70">
                Every Bloc & Brush project is personally reviewed by our
                founders. It's why our quality stays consistent, our timelines
                are honoured and our clients keep coming back.
              </p>
            </div>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
            {[
              {
                name: "Founder & Managing Director",
                role: "Design · Delivery · Client Relationships",
                img: "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=1200&q=85",
                bio: "Leads project design, client relationships and overall delivery — with a hands-on approach to every high-value project the team takes on across Dubai.",
              },
              {
                name: "Co-Founder & Operations Director",
                role: "Execution · Quality · Site Management",
                img: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&w=1200&q=85",
                bio: "Runs day-to-day operations across fit-out, MEP, maintenance and cleaning divisions — the final signoff on every quality inspection before handover.",
              },
            ].map((p) => (
              <Reveal key={p.name}>
                <div className="group bg-[#F5F5F5] rounded-3xl overflow-hidden hover:-translate-y-1 transition-transform duration-500">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={p.img}
                      alt={p.name}
                      className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-[1.4s] ease-out"
                    />
                  </div>
                  <div className="p-8 lg:p-10">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-[#13294B] font-semibold">
                      {p.role}
                    </div>
                    <h3 className="font-display mt-3 text-2xl font-bold text-[#1C1C1C]">
                      {p.name}
                    </h3>
                    <p className="mt-4 text-[15px] leading-[1.75] text-[#1C1C1C]/65">
                      {p.bio}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MISSION & VISION */}
      <section className="py-24 lg:py-32 bg-white" data-testid="about-mission-vision">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10">
          <Reveal>
            <div className="h-full bg-[#0B1A33] text-white rounded-3xl p-10 lg:p-14 relative overflow-hidden">
              <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-white/5 blur-2xl" />
              <Target size={32} strokeWidth={1.4} />
              <h3 className="font-display mt-8 text-3xl lg:text-[40px] font-extrabold tracking-tight">
                Our Mission
              </h3>
              <p className="mt-5 text-white/75 text-[17px] leading-[1.7]">
                To create beautifully designed, expertly executed and
                professionally maintained spaces that enhance everyday living
                and deliver lasting value for every client.
              </p>
              <p className="mt-4 text-white/65 text-[15px] leading-relaxed">
                We are committed to providing complete turnkey property
                solutions through exceptional craftsmanship, innovative
                thinking and reliable service.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="h-full bg-[#F5F5F5] rounded-3xl p-10 lg:p-14 relative overflow-hidden">
              <Telescope size={32} strokeWidth={1.4} className="text-[#13294B]" />
              <h3 className="font-display mt-8 text-3xl lg:text-[40px] font-extrabold tracking-tight text-[#1C1C1C]">
                Our Vision
              </h3>
              <p className="mt-5 text-[#1C1C1C]/75 text-[17px] leading-[1.7]">
                To become Dubai's most trusted turnkey property solutions
                company by combining design, construction, maintenance and
                facility services into one seamless customer experience.
              </p>
              <p className="mt-4 text-[#1C1C1C]/65 text-[15px] leading-relaxed">
                Our goal is to build long-term partnerships rather than
                one-time projects.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-24 lg:py-32 bg-[#F5F5F5]" data-testid="about-values">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.22em] text-[#707070]">Our Values</span>
            <h2 className="font-display mt-4 text-4xl lg:text-[48px] font-extrabold leading-[1.05] tracking-tight text-[#1C1C1C] max-w-2xl">
              The Principles That
              <br />
              <span className="text-[#13294B]">Guide Every Project.</span>
            </h2>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {VALUES.map((v, i) => {
              const Icon = v.icon;
              return (
                <Reveal key={v.title} delay={i * 0.06}>
                  <div className="group h-full bg-white p-8 lg:p-10 rounded-2xl border border-black/5 hover:border-[#13294B]/30 hover:-translate-y-1.5 hover:shadow-[0_24px_50px_rgba(19,41,75,0.10)] transition-all duration-500">
                    <div className="w-12 h-12 rounded-xl bg-[#13294B]/5 flex items-center justify-center text-[#13294B] group-hover:bg-[#13294B] group-hover:text-white transition-colors duration-500">
                      <Icon size={22} strokeWidth={1.6} />
                    </div>
                    <h3 className="font-display mt-7 text-xl font-bold tracking-tight text-[#1C1C1C]">{v.title}</h3>
                    <p className="mt-3 text-[15px] leading-relaxed text-[#1C1C1C]/65">{v.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="py-24 lg:py-32 bg-[#F5F5F5]" data-testid="about-industries">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <Reveal>
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
              <div className="max-w-3xl">
                <span className="text-xs uppercase tracking-[0.22em] text-[#707070]">Who We Serve</span>
                <h2 className="font-display mt-4 text-4xl lg:text-[48px] font-extrabold leading-[1.05] tracking-tight text-[#1C1C1C]">
                  Industries & Clients We
                  <br />
                  <span className="text-[#13294B]">Proudly Support.</span>
                </h2>
              </div>
            </div>
          </Reveal>
          <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {INDUSTRIES.map((ind, i) => {
              const Icon = ind.icon;
              return (
                <Reveal key={ind.label} delay={(i % 4) * 0.05}>
                  <div className="group relative overflow-hidden rounded-2xl aspect-[4/5] bg-[#0B1A33]">
                    <img
                      src={ind.img}
                      alt={ind.label}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-[1.06] transition-all duration-[1.2s] ease-out"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0B1A33] via-[#0B1A33]/35 to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 p-5 text-white flex items-center gap-3">
                      <Icon size={18} strokeWidth={1.6} className="shrink-0" />
                      <span className="font-display text-[15px] font-semibold">{ind.label}</span>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* QUALITY PROMISE */}
      <section className="py-24 lg:py-32 bg-white" data-testid="about-quality">
        <div className="container mx-auto px-6 lg:px-12 max-w-5xl text-center">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.22em] text-[#707070]">Quality Promise</span>
            <h2 className="font-display mt-4 text-4xl lg:text-[56px] font-extrabold leading-[1.05] tracking-tight text-[#1C1C1C]">
              Committed To <span className="text-[#13294B]">Excellence.</span>
            </h2>
            <p className="mt-7 text-[18px] leading-[1.75] text-[#1C1C1C]/70 max-w-3xl mx-auto">
              Quality is never an afterthought. Every project undergoes detailed
              planning, professional execution and multiple quality inspections
              before handover. From selecting materials to final finishing
              touches, we maintain strict standards that ensure every project
              reflects our commitment to excellence.
            </p>
          </Reveal>
        </div>
      </section>

      {/* STATS */}
      <section className="py-24 lg:py-32 bg-[#0B1A33] text-white relative overflow-hidden" data-testid="about-stats">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <Reveal>
            <div className="max-w-3xl">
              <span className="text-xs uppercase tracking-[0.22em] text-white/55">Why Dubai Trusts Us</span>
              <h2 className="font-display mt-4 text-4xl lg:text-[48px] font-extrabold leading-[1.05] tracking-tight">
                Numbers That Speak
                <br />
                <span className="text-white/65">For Themselves.</span>
              </h2>
            </div>
          </Reveal>
          <div className="mt-16 grid grid-cols-2 lg:grid-cols-3 gap-y-12 gap-x-6">
            {[
              { v: 320, suf: "+", label: "Projects Delivered" },
              { v: 145, suf: "+", label: "Properties Furnished" },
              { v: 4800, suf: "+", label: "Maintenance Requests Completed" },
              { v: 2200, suf: "+", label: "Cleaning Jobs Completed" },
              { v: 98, suf: "%", label: "Client Satisfaction" },
              { v: 24, suf: "h", label: "Average Response Time" },
            ].map((s) => (
              <div key={s.label}>
                <div className="font-display text-4xl lg:text-[56px] font-extrabold tracking-tight">
                  <CountUp end={s.v} suffix={s.suf} />
                </div>
                <div className="mt-2 text-xs uppercase tracking-[0.18em] text-white/55">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS TIMELINE */}
      <section className="py-24 lg:py-32 bg-white" data-testid="about-process">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <Reveal>
            <div className="max-w-3xl">
              <span className="text-xs uppercase tracking-[0.22em] text-[#707070]">Our Process</span>
              <h2 className="font-display mt-4 text-4xl lg:text-[48px] font-extrabold leading-[1.05] tracking-tight text-[#1C1C1C]">
                From First Call
                <br />
                <span className="text-[#13294B]">To Long-Term Aftercare.</span>
              </h2>
            </div>
          </Reveal>
          <div className="mt-16 relative">
            <div className="hidden lg:block absolute top-[14px] left-0 right-0 h-px bg-[#13294B]/10" />
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-y-10 gap-x-6">
              {PROCESS.map((step, i) => (
                <Reveal key={step} delay={i * 0.06}>
                  <div className="relative">
                    <div className="w-7 h-7 rounded-full bg-[#13294B] text-white text-xs font-bold flex items-center justify-center">
                      {i + 1}
                    </div>
                    <h3 className="font-display mt-5 text-base lg:text-[17px] font-bold tracking-tight text-[#1C1C1C]">
                      {step}
                    </h3>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <FinalCTA onQuoteClick={() => setQuoteOpen(true)} />
      <Footer onQuoteClick={() => setQuoteOpen(true)} />
      <WhatsAppFloat />
      <QuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} />
    </div>
  );
}
